import { Col, Row, Space, Tag } from "antd"
import { Microsite, MicrositeCluster } from "../../../../domain/entities/Microsite"
import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import { FormField } from "../../../components/Form/FormField"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { DinamicSelectorWeightForm } from "../../sellersServices/components/DinamicSelectorWeightForm"
import { MicrositesViewModel } from "../MicrositesViewModel"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { CategoriesViewModel } from "../../categories/CategoriesViewModel"
import { useEffect } from "react"
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd"
import { HolderOutlined } from "@ant-design/icons"
import { toJS } from "mobx"

interface Props {
	inputDisabled?: boolean
	onChange?: (value: Partial<Microsite>) => void
}

const MicrositeInformationFormSection = ({ inputDisabled, onChange }: Props) => {
	const { t } = useTranslation("microsites", { keyPrefix: "form.micrositeInfo" })
	const viewModel = useContainerInjection<MicrositesViewModel>("MicrositesViewModel")
	const categoriesViewModel = useContainerInjection<CategoriesViewModel>("CategoriesViewModel")

	const formData = toJS(viewModel.formData || {})
	const availableLanguages = formData?.availableLanguages || [formData?.lang || "es"]

	useEffect(() => {
		if (categoriesViewModel.categories.length === 0) {
			categoriesViewModel.fetchCategoriesData()
		}
	}, [])

	return (
		<>
			<Row gutter={[24, 0]}>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						key={"sellerName"}
						label={t("partner")}
						name={"sellerName"}
						mandatory
						onChange={(sellerName: string) => onChange && onChange({ sellerName })}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						key={"slug"}
						label={t("slug")}
						name={"slug"}
						mandatory
						onChange={(slug: string) => onChange && onChange({ slug })}
					/>
				</Col>
			</Row>

			{availableLanguages.map((lang: string) => {
				const langKey = lang.toLowerCase()
				const fieldName =
					langKey === "es-es"
						? "popupText"
						: langKey === "ca-es"
						? "popupTextCa"
						: langKey === "es-mx"
						? "popupTextMx"
						: langKey === "en-us"
						? "popupTextEn"
						: `popupText_${langKey}`

				const fieldValue = formData?.[fieldName as keyof Microsite] as string | undefined

				return (
					<Row gutter={[24, 0]} key={fieldName}>
						<Col xl={24} md={24} xs={24}>
							<FormField.Input
								key={fieldName}
								label={`${t("popUpText")}${langKey ? ` (${langKey.toUpperCase()})` : ""}`}
								name={fieldName}
								value={fieldValue}
								inputType={InputType.TEXTAREA}
								mandatory
								onChange={(value: string) => onChange && onChange({ [fieldName]: value })}
							/>
						</Col>
					</Row>
				)
			})}
			<Row gutter={[24, 0]}>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						key={"fontType"}
						label={t("fontType")}
						name={"fontType"}
						onChange={(fontType: string) => onChange && onChange({ fontType })}
					/>
				</Col>
				<Col span={24} xl={12} md={12} xs={24}>
					<FormField.Input
						key={"externalLink"}
						label={t("externalLink")}
						name={"externalLink"}
						onChange={(externalLink: string) => onChange && onChange({ externalLink })}
					/>
				</Col>
				<Col xl={24} md={24} xs={24} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
					<DinamicSelectorWeightForm<MicrositeCluster>
						label={t("clusterSelectorLabel")}
						name="micrositeCluster"
						onAdd={msiteCluster => {
							if (!msiteCluster.id || msiteCluster.weight === undefined || msiteCluster.weight === null)
								return

							const currentClusters = formData?.clusters || []
							const targetWeight = Number(msiteCluster.weight)

							const newDetail: MicrositeCluster = {
								clusterId: String(msiteCluster.id),
								weight: targetWeight,
								status: true
							}

							const updatedClusters = [...currentClusters, newDetail]
								.sort((a, b) => Number(a.weight ?? 0) - Number(b.weight ?? 0))
								.map((cluster, index) => ({
									...cluster,
									weight: index + 1
								}))

							onChange && onChange({ clusters: updatedClusters })
						}}
						data={categoriesViewModel.categories.filter(
							category => !formData?.clusters?.find(cluster => cluster.clusterId === category.id)
						)}
						loading={categoriesViewModel.isLoading}
						disabled={categoriesViewModel.isLoading}
					/>
					<DragDropContext
						onDragEnd={(result: DropResult) => {
							if (!result.destination) return
							if (result.source.index === result.destination.index) return

							const updatedClusters = viewModel.reorderClusters(
								result.source.index,
								result.destination.index
							)

							if (onChange) {
								onChange({ clusters: updatedClusters })
							} else {
								console.log("onChange is not defined")
							}
						}}
					>
						<Droppable droppableId="clusters" direction="horizontal">
							{(provided, snapshot) => (
								<div
									ref={provided.innerRef}
									{...provided.droppableProps}
									style={{
										display: "flex",
										flexWrap: "wrap",
										alignItems: "flex-start",
										gap: "8px",
										minHeight: 40,
										padding: "4px",
										backgroundColor: snapshot.isDraggingOver ? "#f0f0f0" : "transparent"
									}}
								>
									{formData?.clusters?.map((cat, idx) => {
										const clusterDetail = categoriesViewModel.categories.find(
											category => category.id == cat.clusterId
										)
										return clusterDetail ? (
											<Draggable
												key={`cluster-${cat.clusterId}`}
												draggableId={`cluster-${cat.clusterId}`}
												index={idx}
											>
												{(provided, snapshot) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
														style={{
															...provided.draggableProps.style
														}}
													>
														<Tag
															{...provided.dragHandleProps}
															closable
															style={{
																userSelect: "none",
																height: 30,
																display: "flex",
																alignItems: "center",
																borderRadius: 10,
																gap: 8,
																cursor: "grab",
																margin: 0,
																backgroundColor: snapshot.isDragging
																	? "#e6f7ff"
																	: undefined,
																border: snapshot.isDragging
																	? "2px solid #1890ff"
																	: undefined,
																boxShadow: snapshot.isDragging
																	? "0 5px 10px rgba(0,0,0,0.2)"
																	: undefined
															}}
															onClose={e => {
																e.stopPropagation()
																viewModel.removeCluster(idx)
															}}
														>
															<HolderOutlined style={{ marginRight: 4 }} />
															{`${clusterDetail.id} - ${clusterDetail?.label} (${cat.weight})`}
														</Tag>
													</div>
												)}
											</Draggable>
										) : null
									})}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</Col>
			</Row>
		</>
	)
}

export default observer(MicrositeInformationFormSection)
